import { Link } from 'react-router-dom';
import { Link2 } from 'lucide-react';
import { useHubProjects } from '../hub/api';

/**
 * "This client is a hub/accounts organization too."
 *
 * **Link state is ours; the display name is theirs.** The link itself is
 * `Client.hubOrgId`, shipped on `/api/clients/:id`, so the badge renders for a
 * client whose accounts org has no hub projects at all and keeps rendering when
 * the hub is unreachable. The hub is called only to turn that opaque cuid into
 * a name a human recognises; if the call fails or the org is not among the
 * rows, the id is printed rather than the badge disappearing.
 *
 * (Before this it was derived from `GET /api/hub/projects` alone, which meant a
 * real, recorded link was invisible whenever the hub was down or the org had no
 * agreements — the page claimed no connection where the database held one.)
 */
export function ClientHubOrg({ hubOrgId }: { hubOrgId: string | null }) {
  const hub = useHubProjects({ enabled: !!hubOrgId });

  if (!hubOrgId) return null;

  const name =
    (hub.data?.projects ?? []).find((r) => r.clientOrgId === hubOrgId)?.clientOrgName ?? null;

  return (
    <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-body-sm text-grey-500">
      {/* Glyph and sentence stay one unit so the icon cannot wrap alone. */}
      <span className="flex min-w-0 items-start gap-2">
        <Link2 className="mt-0.5 h-4 w-4 shrink-0 text-accent-700" aria-hidden />
        <span>
          Linked to hub organization{' '}
          <span className={name ? 'font-semibold text-secondary' : 'num text-grey-500'}>
            {name ?? hubOrgId}
          </span>
        </span>
      </span>
      <Link to="/settings?tab=hub" className="text-accent hover:underline">
        Hub settings
      </Link>
    </p>
  );
}
